import React, { useState, useMemo } from "react";
import { useGetList, Loading, Title, ShowButton } from "react-admin";
import {
  Grid,
  Typography,
  TextField,
  Tabs,
  Tab,
  Box,
  Avatar,
  Paper,
  Card,
  InputAdornment,
  Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import InventoryIcon from "@mui/icons-material/Inventory";
import CategoryIcon from "@mui/icons-material/Category";
import PaymentsIcon from "@mui/icons-material/Payments";
import DownloadIcon from "@mui/icons-material/Download";

// متغير خارجي لحفظ مكان الوصول
let globalVisibleCount = 10;

// -------- Helpers --------
const normalizeCat = (s = "") =>
  String(s).trim().replace(/\s+/g, " ").replace(/\.+/g, "").trim();

const getMainCategory = (cat = "") => {
  const c = normalizeCat(cat);
  if (!c) return "Other";

  // أطفال
  if (c === "أطفال" || c.includes("أطفال")) return "أطفال";

  // أحذية
  if (c.includes("أحذية") || c === "أحذية") return "أحذية";

  // ساعات
  if (c.includes("ساعات") || c.includes("أطقم ساعات")) return "ساعات";

  // إكسسوارات
  if (c.includes("إكسسوارات") || c.includes("أقراط") || c.includes("أساور"))
    return "إكسسوارات";

  // ملابس
  if (
    c.includes("ملابس") ||
    c.includes("تي شيرت") ||
    c.includes("تنسيقات") ||
    c.includes("رجالية") ||
    c.includes("الفتيات") ||
    c.includes("المراهقات")
  ) {
    return "ملابس";
  }

  // هوايات / مناظير
  if (c.includes("منظار") || c.includes("تلسكوب") || c.includes("مناظير"))
    return "هوايات/مناظير";

  return "Other";
};

const MyDashboard = () => {
  const { data, isLoading } = useGetList("products", {
    pagination: { page: 1, perPage: 5000 },
  });

  const safeData = data || [];

  const [search, setSearch] = useState("");
  const [mainCat, setMainCat] = useState("all"); // ✅ سطر واحد فقط
  const [visibleCount, setVisibleCount] = useState(globalVisibleCount);

  // استخراج Main Categories من الداتا
  const mainCategories = useMemo(() => {
    const mains = safeData.map((x) => getMainCategory(x.category));
    return [...new Set(mains.filter(Boolean))].sort();
  }, [safeData]);

  // فلترة حسب main + search
  const filteredData = useMemo(() => {
    const s = search.toLowerCase();

    return safeData.filter((item) => {
      const title = (item.title || "").toLowerCase();
      const cat = normalizeCat(item.category);

      const matchesSearch = title.includes(s);

      // ✅ الفلترة على أساس main category فقط
      const matchesMain =
        mainCat === "all" ? true : getMainCategory(cat) === mainCat;

      return matchesSearch && matchesMain;
    });
  }, [safeData, search, mainCat]);

  // displayed
  const displayedData = useMemo(() => {
    return filteredData.slice(0, visibleCount);
  }, [filteredData, visibleCount]);

  // avg
  const avgPrice = useMemo(() => {
    if (displayedData.length === 0) return "0.00";
    const sum = displayedData.reduce(
      (acc, curr) => acc + parseFloat(curr.price || 0),
      0
    );
    return (sum / displayedData.length).toFixed(2);
  }, [displayedData]);

  const handleLoadMore = () => {
    globalVisibleCount = visibleCount + 10;
    setVisibleCount(globalVisibleCount);
  };

  if (isLoading) return <Loading />;

  return (
    <Box p={4} sx={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      <Title title="Inventory Dashboard" />

      {/* الإحصائيات */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 5,
              background: "linear-gradient(135deg, #1e3a8a, #3b82f6)",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="subtitle2">VIEWING</Typography>
              <Typography variant="h4" fontWeight={800}>
                {displayedData.length}
              </Typography>
            </Box>
            <InventoryIcon fontSize="large" />
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 5,
              background: "linear-gradient(135deg, #064e3b, #10b981)",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="subtitle2">CATEGORIES</Typography>
              <Typography variant="h4" fontWeight={800}>
                {mainCategories.length}
              </Typography>
            </Box>
            <CategoryIcon fontSize="large" />
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 5,
              background: "linear-gradient(135deg, #831843, #ec4899)",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="subtitle2">AVG PRICE</Typography>
              <Typography variant="h4" fontWeight={800}>
                {avgPrice} JOD
              </Typography>
            </Box>
            <PaymentsIcon fontSize="large" />
          </Paper>
        </Grid>
      </Grid>

      {/* البحث والتحميل */}
      <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
        <TextField
          fullWidth
          placeholder="Search..."
          sx={{ bgcolor: "white", borderRadius: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            globalVisibleCount = 10;
            setVisibleCount(10);
          }}
        />

        {visibleCount < filteredData.length && (
          <Button
            variant="contained"
            onClick={handleLoadMore}
            startIcon={<DownloadIcon />}
            sx={{ borderRadius: 3, px: 4, minWidth: "160px" }}
          >
            Load More
          </Button>
        )}
      </Box>

      {/* ✅ سطر Tabs واحد فقط */}
      <Tabs
        value={mainCat}
        onChange={(e, val) => {
          setMainCat(val);
          globalVisibleCount = 10;
          setVisibleCount(10);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        <Tab label="ALL" value="all" sx={{ fontWeight: "bold" }} />
        {mainCategories.map((m) => (
          <Tab key={m} label={m} value={m} sx={{ fontWeight: "bold" }} />
        ))}
      </Tabs>

      {/* الجدول */}
      <Card sx={{ borderRadius: 5, boxShadow: 3, overflow: "hidden" }}>
        <div style={{ height: "auto", width: "100%" }}>
          <DataGrid
            rows={displayedData}
            getRowId={(row) => row.id}
            rowHeight={100}
            columns={[
              {
                field: "image",
                headerName: "Img",
                width: 90,
                renderCell: (p) => (
                  <Avatar
                    src={p.value}
                    variant="rounded"
                    sx={{ width: 60, height: 60 }}
                  />
                ),
              },
              {
                field: "title",
                headerName: "Product",
                flex: 1,
                renderCell: (p) => (
                  <Box py={1}>
                    <Typography fontWeight={700} noWrap>
                      {p.value}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {p.row.category || ""}
                    </Typography>
                  </Box>
                ),
              },
              {
                field: "price",
                headerName: "Price",
                width: 110,
                renderCell: (p) => (
                  <Typography fontWeight={800} color="green">
                    {p.value} JOD
                  </Typography>
                ),
              },
              {
                field: "id",
                headerName: "Action",
                width: 140,
                renderCell: (p) => (
                  <ShowButton resource="products" record={p.row} label="View" />
                ),
              },
            ]}
            autoHeight
            hideFooter
            disableRowSelectionOnClick
            isRowSelectable={() => false}
          />
        </div>
      </Card>
    </Box>
  );
};

export default MyDashboard;
